import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export type CollectionReportHistoryItem = {
  reportId: string;
  title?: string;
  createdAt?: string;
  createdBy?: string;
};

export type GenerateCollectionReportPayload = {
  title: string;
  remarks: string;
  generatedAt?: string;
  toiText?: string;
  mapImage: Blob;
  aoiMapImage?: Blob;
  selectionsSummaryText?: string;
};

@Injectable({
  providedIn: 'root',
})
export class CollectionReportsService {
  private readonly server = environment.eeTaskServer;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
  ) {}

  private async getUserId(): Promise<string> {
    const existingId = (this.authService.userValue as any)?.id;
    if (existingId && typeof existingId === 'string') {
      return existingId;
    }

    try {
      const user: any = await firstValueFrom(
        this.authService.user.pipe(
          filter((u: any) => !!u?.id),
          take(1),
          timeout(5000),
        ),
      );
      return user.id;
    } catch {
      throw new Error('User session is not ready. Please try again.');
    }
  }

  async list(taskId: string): Promise<CollectionReportHistoryItem[]> {
    const userId = await this.getUserId();
    const resp: any = await firstValueFrom(
      this.http.get(`${this.server}/tasks/collectionreports/${userId}/${taskId}`),
    );
    return Array.isArray(resp?.result) ? resp.result : [];
  }

  async generate(taskId: string, payload: GenerateCollectionReportPayload): Promise<string | undefined> {
    const userId = await this.getUserId();

    const form = new FormData();
    form.append('title', payload.title || '');
    form.append('remarks', payload.remarks || '');
    if (payload.generatedAt) {
      form.append('generatedAt', payload.generatedAt);
    }
    if (payload.toiText) {
      form.append('toiText', payload.toiText);
    }
    if (payload.selectionsSummaryText) {
      form.append('selectionsSummaryText', payload.selectionsSummaryText);
    }
    // Backend expects field name: mapImage
    form.append('mapImage', payload.mapImage, `collection_${taskId}.jpg`);

    // Optional AOI-only image
    if (payload.aoiMapImage) {
      form.append('aoiMapImage', payload.aoiMapImage, `collection_${taskId}_aoi.jpg`);
    }

    const resp: any = await firstValueFrom(
      this.http.post(`${this.server}/tasks/collectionreports/${userId}/${taskId}`, form),
    );
    return resp?.result?.reportId;
  }

  async getSignedUrl(taskId: string, reportId: string): Promise<string | undefined> {
    const userId = await this.getUserId();
    const resp: any = await firstValueFrom(
      this.http.get(`${this.server}/tasks/collectionreports/${userId}/${taskId}/${reportId}/signedUrl`),
    );
    return resp?.result;
  }

  async getSignedUrlWithDisposition(
    taskId: string,
    reportId: string,
    disposition: 'inline' | 'attachment',
  ): Promise<string | undefined> {
    const userId = await this.getUserId();
    const resp: any = await firstValueFrom(
      this.http.get(
        `${this.server}/tasks/collectionreports/${userId}/${taskId}/${reportId}/signedUrl?disposition=${encodeURIComponent(
          disposition,
        )}`,
      ),
    );
    return resp?.result;
  }

  async delete(taskId: string, reportId: string): Promise<void> {
    const userId = await this.getUserId();
    await firstValueFrom(
      this.http.delete(`${this.server}/tasks/collectionreports/${userId}/${taskId}/${reportId}`),
    );
  }
}
